// ProtectedRoute — проверяет /me/.
//
// Поведение:
//   - me === null (не залогинен)            → редирект на /login с returnTo.
//   - me.is_active === false (деактивирован) → редирект на /deactivated.
//   - me ok                                  → рендерит Outlet (children-роуты).
//   - me.is_superuser === false на /admin/*  → 403 (рендерит NotAuthorized).
//
// Deep-link return: при редиректе на /login сохраняем текущий путь в state.returnTo.
// LoginPage после успешного login дёргает navigate(returnTo ?? "/").
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Spin, Result } from "antd";
import { useMe } from "@/hooks/useAuth";
import type { MeResponse } from "@/api/types";

interface Props {
  /** Если true — требует is_superuser, иначе 403. */
  requireSuperuser?: boolean;
  /** Кастомный рендер для дочерних: получает me. По умолчанию — Outlet. */
  render?: (me: MeResponse) => React.ReactNode;
}

export function ProtectedRoute({ requireSuperuser, render }: Props) {
  const location = useLocation();
  const { data, isPending } = useMe();

  if (isPending) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" description="Загрузка..." />
      </div>
    );
  }

  if (!data) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: location.pathname + location.search }}
      />
    );
  }

  // Backend возвращает is_active в /me/ (см. apps/accounts/views.py:MeView).
  // Если is_active=false, отправляем на /deactivated. Если поле отсутствует
  // (старый контракт) — считаем пользователя активным (поведение по умолчанию).
  if (data.is_active === false) {
    return <Navigate to="/deactivated" replace />;
  }

  if (requireSuperuser && !data.is_superuser) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="Эта страница доступна только администратору."
      />
    );
  }

  return <>{render ? render(data) : <Outlet />}</>;
}
